import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@services/auth/';
import { BrsTokenService } from '@services/brs-token/';
import { CredsEndPoint, Endpoint, EndpointService } from '@services/endpoints';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { config } from '../../../environments/config';
import { isJSON } from '../../../shared/utilities';
import { I18nService } from '../../i18n/services';
import { ApiResponseStringsService } from '../api-response-strings';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '../feedback';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	constructor(
		private authService: AuthService,
		private brsTokenService: BrsTokenService,
		private apiResponseStringsService: ApiResponseStringsService,
		private endpointService: EndpointService,
		private feedbackService: FeedbackService,
		private i18nService: I18nService,
		private router: Router
	) {}

	public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const apiCall: boolean = this.endpointService.isApiCall(request.url);
		const endpoint: Endpoint = this.endpointService.getEndpoint(request.url);
		const brsInit: boolean = this.endpointService.isBrsInit(request.url);
		const brsCall: boolean = this.endpointService.isBrsCall(request.url);
	
		if ((apiCall && CredsEndPoint(endpoint)) || brsInit) {
			return new Observable<HttpEvent<any>>((observer) => {
				this.authService.getToken().subscribe(
					(token: string) => {
						const customReq = request.clone({
							setHeaders: {
								Authorization: `Bearer ${token}`
							}
						});

						next.handle(customReq)
							.pipe(
								catchError((err: any) => {
									if (err.status === 401 && err?.error?.message !== 'Unauthorized') this.handle401();

									return throwError(this.decorateError(customReq, err));
								})
							)
							.subscribe(
								(result) => observer.next(result),
								(error) => observer.error(error)
							);
					},
					() => {
						next.handle(request).subscribe(
							(result: any) => observer.next(result),
							(error: any) => observer.error(this.decorateError(request, error))
						);
					}
				);
			});
		} else if (apiCall) {
			// add Api-Key header to unauthenticated endpoints
			return next.handle(request.clone({ headers: request.headers.set('Api-Key', config.clientId) })).pipe(
				catchError((err: any) => {
					return throwError(this.decorateError(request, err));
				})
			);
			
		} else if (brsCall) {
			return new Observable<HttpEvent<any>>((observer) => {
				this.brsTokenService.getBrsToken().subscribe(
					(brsToken: string) => {
						const customReq = request.clone({
							setHeaders: {
								Authorization: `Bearer ${brsToken}`
							}
						});

						next.handle(customReq)
							.pipe(
								catchError((err: any) => {
									// TODO: Handle 401 for brsCalls differently
									if (err.status === 401 && err?.error?.message !== 'Unauthorized') this.handle401();

									return throwError(this.decorateError(customReq, err));
								})
							)
							.subscribe(
								(result) => observer.next(result),
								(error) => observer.error(error)
							);
					},
					() => {
						next.handle(request).subscribe(
							(result: any) => observer.next(result),
							(error: any) => observer.error(this.decorateError(request, error))
						);
					}
				);
			});
		} else {
			return next.handle(request).pipe(
				catchError((err: any) => {
					return throwError(this.decorateError(request, err));
				})
			);
		}
	}

	/**
	 * Validates if errors is a Portal or Whats error and gets translated error message if possible.
	 * If Whats style it will create a new HttpErrorResponse with error in Portal style to allow
	 * translated messages to be used in App.
	 *
	 * @param request HttpRequest
	 * @param response HttpErrorResponse with either Portal style API errors response or Whats
	 */
	private decorateError(request: HttpRequest<any>, response: HttpErrorResponse): HttpErrorResponse {
		if (this.endpointService.isApiCall(request.url) && typeof response.error === 'object') {
			response.error.errorMessage = this.apiResponseStringsService.stringForError(response);
			return response;
		} else if (isJSON(response.error) || typeof response.error === 'object') {
			let error = response.error;

			if (isJSON(response.error)) error = JSON.parse(response.error);

			return new HttpErrorResponse({
				error: {
					errorMessage: this.apiResponseStringsService.stringForError(response),
					errorCode: response.status,
					...error
				},
				headers: response.headers,
				status: response.status,
				statusText: response.statusText,
				url: response.url || undefined
			});
		}

		return response;
	}

	private handle401(): void {
		this.authService.logout(this.router.routerState.snapshot.url);

		this.feedbackService.plain({
			title: FeedbackTitle.error,
			message: this.i18nService.get(
				'Oops! Looks like your session has expired, please log in to try again.',
				'app.api.response.general-session-expired'
			),
			style: FeedbackStyle.error
		});
	}
}
